/* Commandes de quantité du panier — Scoop by Coralie

   Module unique, chargé sur toutes les pages : la page `/cart` et le tiroir
   panier partagent exactement le même code. Il était auparavant inline dans
   `sections/cart-ajax-fix.liquid`, donc absent des fiches produit.

   Il n'y a qu'un seul module de ce type dans le thème. En ajouter un second
   recréerait les doubles `+` / `−` corrigés en V17 (voir docs/11_BUGS_KNOWN.md).

   L'écoute est déléguée sur `document` : la section panier est remplacée en
   entier à chaque rendu, et le tiroir est injecté après coup — des écouteurs
   posés sur le formulaire seraient perdus au premier remplacement.
*/
(() => {
  /* Lot 103 — l'en-tête du panier n'est plus retouché ici.

     Ce module retirait l'eyebrow et le paragraphe du DOM, puis réécrivait le
     `h1` avec le texte que `main-cart.liquid` y avait déjà mis. Les trois
     éléments sont maintenant rendus — ou non rendus — une seule fois, en
     Liquid. Ce module ne s'occupe plus que des quantités, du stock et du code
     promo. */

  /* V15 — ce module ne rend plus le panier.

     Le rendu appartient désormais à `assets/sbc-cadeaux.js` : il réconcilie les
     cadeaux, puis redemande la section entière à Shopify. Deux pipelines de
     rendu concurrents écrivaient tour à tour dans le même DOM et produisaient
     des états divergents (lignes à jour d'un côté, total figé de l'autre).
     Il n'en reste qu'un. Ce module ne garde que les commandes de quantité,
     et se repeint quand le moteur annonce un panier à jour. */

  /* V19 — sélecteur des champs de quantité.

     La section panier nomme désormais ces champs `updates[<clé de ligne>]` et
     non plus `updates[]` : la forme tableau dépend de l'ordre des lignes du
     panier, ce qui interdisait d'afficher les cadeaux avant les achats. Le
     sélecteur par préfixe reconnaît les deux formes — aucune section héritée
     n'est cassée si elle rend encore l'ancienne. */
  /* Message d'erreur ou d'état, affiché là où la cliente regarde. Le nœud est
     rendu par le tiroir ; sur `/cart` il n'existe pas et l'annonce est
     simplement ignorée. */
  const annoncer = (texte) => {
    document.querySelectorAll('[data-sbc-cart-erreur]').forEach((n) => {
      n.textContent = texte || '';
      n.hidden = !texte;
    });
  };

  /* Variante de la ligne dont la quantité vient d'être modifiée. La clé de
     ligne Shopify change à chaque mutation ; la variante, non. */
  let focusARendre = null;
  /* Lot 132 — quelle commande de la ligne reprend le focus après le rendu :
     'minus', 'plus', ou null pour le champ de saisie. Sans cette mémoire, un
     clic sur « + » laissait le focus nulle part : la restauration par
     identifiant de `sbc-panier.js` ne trouve rien, les deux boutons étant
     créés par `enhanceSteppers()` sans `id` ni `name`. */
  let focusCommande = null;

  const formulaire = () => document.getElementById('CartForm');

  const syncMinus = (stepper) => {
    const input = stepper?.querySelector('input[name^="updates["]');
    const minus = stepper?.querySelector('[data-sbc-qty="minus"]');
    if (!input || !minus) return;
    const quantity = Math.max(0, parseInt(input.value || '0', 10));
    minus.classList.toggle('is-remove', quantity <= 1);
    minus.setAttribute('aria-label', quantity <= 1 ? 'Retirer cet article' : 'Diminuer la quantité');
  };

  /* Une ligne cadeau n'a pas de bloc `.quantity` : elle ne reçoit donc aucun
     stepper, sans qu'il faille l'exclure ici. Depuis le lot 148 (décision
     D19), la vidéo n'est plus une ligne : c'est une valeur d'option de la
     ligne de scoop, qui se compte et se règle comme le reste de la ligne. */
  const enhanceSteppers = () => {
    const form = formulaire();
    if (!form) return;
    form.classList.add('sbc-cart-auto-quantity');
    form.classList.remove('is-cart-updating');
    form.removeAttribute('aria-busy');
    form.querySelectorAll('.cart-line__controls .quantity').forEach((quantity) => {
      const input = quantity.querySelector('input[name^="updates["]');
      if (!input || quantity.dataset.sbcStepper === 'true') return;
      quantity.dataset.sbcStepper = 'true';
      quantity.classList.add('sbc-cart-quantity');

      const minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'sbc-cart-quantity__button';
      minus.dataset.sbcQty = 'minus';
      minus.innerHTML = '<span aria-hidden="true">−</span>';

      const plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'sbc-cart-quantity__button';
      plus.dataset.sbcQty = 'plus';
      plus.setAttribute('aria-label', 'Augmenter la quantité');
      plus.innerHTML = '<span aria-hidden="true">+</span>';

      input.insertAdjacentElement('beforebegin', minus);
      input.insertAdjacentElement('afterend', plus);
      syncMinus(quantity);
    });
  };

  /* ------------------------------------------------------------------
     Ciblage des lignes — V17.

     Une configuration de Scoop peut correspondre à plusieurs lignes Shopify :
     une réduction « Achetez X, obtenez Y » sépare les unités qui satisfont sa
     condition d'achat du reste. Mesuré : porter à 4 une ligne de 3 renvoie
     3 + 1, et l'unité ajoutée atterrit dans la ligne que Shopify choisit,
     pas dans celle qu'on vise. Viser une seule clé donnait donc l'impression
     que le « + » agissait sur une autre ligne.

     Le rendu regroupe désormais ces lignes et publie leurs clés dans
     `data-line-keys` sous la forme `cle:::quantite,cle:::quantite`. Les
     commandes raisonnent sur le **total du groupe** : on demande une quantité
     totale, et on la répartit sur les lignes réelles.

     Les clés sont relues dans le DOM au moment du clic, jamais mémorisées
     dans une fermeture : après chaque rendu de section, elles ont changé.
     ------------------------------------------------------------------ */

  const membresDe = (ligne) => (ligne?.dataset.lineKeys || '')
    .split(',')
    .filter(Boolean)
    .map((brut) => {
      const sep = brut.lastIndexOf(':::');
      return { cle: brut.slice(0, sep), quantite: Math.max(0, parseInt(brut.slice(sep + 3), 10) || 0) };
    })
    .filter((m) => m.cle);

  /* Répartition d'une quantité totale sur les lignes réelles du groupe.
     · en hausse, tout va sur la première ligne — Shopify replacera l'unité
       où sa réduction l'exige, le total demandé est respecté ;
     · en baisse, on vide d'abord les plus petites lignes, ce qui laisse
       intacte la ligne portant l'attribution de la réduction et évite un
       nouveau fractionnement. */
  const repartir = (membres, cible) => {
    const total = membres.reduce((s, m) => s + m.quantite, 0);
    if (cible === total || !membres.length) return [];
    if (cible > total) {
      const premier = membres[0];
      return [{ cle: premier.cle, quantite: premier.quantite + (cible - total) }];
    }
    let aRetirer = total - cible;
    const ordre = [...membres].sort((a, b) => a.quantite - b.quantite);
    const ordres = [];
    for (const m of ordre) {
      if (aRetirer <= 0) break;
      const pris = Math.min(m.quantite, aRetirer);
      ordres.push({ cle: m.cle, quantite: m.quantite - pris });
      aRetirer -= pris;
    }
    return ordres;
  };

  /* Les quantités passent par `cart/change.js`, ligne par ligne, désignée par
     sa clé. La forme tableau de `cart/update.js` s'est révélée dangereuse ici :
     avec une ligne cadeau dans le panier, `{"updates":[1,2]}` a scindé la ligne
     visée en deux lignes de quantité 1 au lieu d'en porter une à 2. Mesuré, pas
     supposé — la trace est au compte rendu. */

  /* Lot 104 — ce module n'écrit plus dans le panier.

     Il envoyait lui-même une requête `/cart/change.js` par ligne, après un
     `setTimeout` de 180 ms, et comptait sur le wrapper `fetch` du moteur de
     cadeaux pour que la réconciliation suive. Trois défauts : les requêtes
     partaient en série (cinq lignes = cinq allers-retours), elles pouvaient se
     croiser avec une réconciliation en cours, et le message d'erreur de
     Shopify — « stock insuffisant » — n'existait que si le wrapper avait bien
     vu passer l'appel.

     Désormais : `SbcPanier.changerBientot(ordres)` groupe les clics rapides
     (debounce de saisie unique, chez le moteur), envoie UNE requête
     `/cart/update.js` pour toutes les lignes, et publie l'erreur éventuelle
     sur `sbc:panier-erreur`, écouté plus bas. */

  const occupe = (etat) => {
    document.querySelectorAll('#CartForm .sbc-cart-quantity').forEach((s) => {
      if (etat) s.setAttribute('aria-busy', 'true');
      else s.removeAttribute('aria-busy');
    });
  };

  const schedule = (ligne, quantiteTotale) => {
    const membres = membresDe(ligne);
    if (!membres.length) return;
    const ordres = repartir(membres, Math.max(0, quantiteTotale));
    if (!ordres.length) return;
    occupe(true);
    if (!window.SbcPanier) return;
    window.SbcPanier.changerBientot(ordres);
  };

  /* Écoute déléguée sur le document : la section panier est remplacée en
     entier à chaque rendu, des écouteurs posés sur le formulaire seraient
     perdus au premier remplacement. */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-sbc-qty]');
    if (!button || !button.closest('#CartForm')) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const stepper = button.closest('.sbc-cart-quantity');
    const input = stepper?.querySelector('input[name^="updates["]');
    const ligne = stepper?.closest('.cart-line');
    if (!input || !ligne) return;

    /* La quantité de référence est celle du groupe, lue dans le DOM courant. */
    const current = Math.max(0, parseInt(ligne.dataset.lineQuantity || input.value || '0', 10));
    const next = button.dataset.sbcQty === 'plus' ? current + 1 : Math.max(0, current - 1);
    if (next === current) return;

    input.value = String(next);
    ligne.dataset.lineQuantity = String(next);
    syncMinus(stepper);
    if (next === 0) ligne.classList.add('is-removing');
    /* La ligne visée se retrouve par sa variante (la clé de ligne Shopify
       change avec la quantité) et la commande par son côté : après le rendu,
       le pouce repart du bouton qu'il vient de presser. */
    focusARendre = ligne.dataset.lineVariant || null;
    focusCommande = button.dataset.sbcQty || null;
    schedule(ligne, next);
  }, true);

  /* ------------------------------------------------------------------
     Application d'une quantité saisie à la main — un seul calculateur.

     Le clavier et le `change` empruntent exactement ce chemin : il n'existe
     pas de second calcul de quantité dans le thème.

     Valeurs refusées et ramenées à la quantité courante du groupe : vide, non
     numérique, négative. `0` reste la règle de suppression existante. Une
     quantité identique n'émet aucune requête.
     ------------------------------------------------------------------ */
  const appliquerQuantite = (input) => {
    const ligne = input.closest('.cart-line');
    if (!ligne) return false;

    const courante = Math.max(0, parseInt(ligne.dataset.lineQuantity || '0', 10));
    const brut = String(input.value || '').trim();
    const nombre = /^\d+$/.test(brut) ? parseInt(brut, 10) : NaN;

    if (!Number.isFinite(nombre)) {
      /* Saisie vide, non numérique ou négative : on rétablit la quantité réelle
         plutôt que d'envoyer au serveur une valeur qui n'a pas de sens. */
      input.value = String(courante);
      annoncer('Quantité invalide. La quantité précédente a été rétablie.');
      return false;
    }

    input.value = String(nombre);
    if (nombre === courante) { annoncer(''); return false; }

    ligne.dataset.lineQuantity = String(nombre);
    syncMinus(input.closest('.sbc-cart-quantity'));
    if (nombre === 0) ligne.classList.add('is-removing');
    /* La ligne visée est retrouvée après le rendu par sa variante : la clé de
       ligne Shopify, elle, change dès que la quantité change. */
    focusARendre = ligne.dataset.lineVariant || null;
    focusCommande = null;
    annoncer('');
    schedule(ligne, nombre);
    return true;
  };

  document.addEventListener('change', (event) => {
    const input = event.target.closest('input[name^="updates["]');
    if (!input || !input.closest('#CartForm')) return;
    if (input.type === 'hidden') return;

    event.stopImmediatePropagation();
    appliquerQuantite(input);
  }, true);

  /* ------------------------------------------------------------------
     V19.1 — touche Entrée dans un champ de quantité du tiroir.

     Le champ vit dans `<form action="/cart" method="post">`. Entrée y déclenche
     la soumission implicite du formulaire : le navigateur quittait le tiroir et
     ouvrait `/cart`. Reproduit le 20/08/2026 — l'URL passait de `/` à `/cart`.

     L'interception est volontairement étroite :

     · elle ne vise que la touche Entrée ;
     · seulement depuis un champ de quantité visible ;
     · seulement à l'intérieur du tiroir.

     Sur `/cart`, Entrée continue donc de soumettre le formulaire : c'est le
     repli natif de la page, et il doit rester fonctionnel. Un clic réel sur
     « Passer au paiement sécurisé » n'est jamais intercepté — aucune
     soumission n'est bloquée globalement, seule cette origine précise l'est.
     ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
     V19.1 — « Supprimer » dans le tiroir.

     C'est un lien natif vers `item.url_to_remove`, donc une navigation vers
     `/cart`. Même famille de bogue que la touche Entrée, et même règle : dans
     le tiroir, la suppression passe par le contrôleur Ajax ; sur `/cart`, le
     lien reste un lien — c'est le repli qui fonctionne sans JavaScript.

     La suppression vise le **groupe** entier via `schedule(ligne, 0)`, donc les
     mêmes clés que les boutons `+` et `−` : le regroupement V17 est préservé.
     ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const lien = event.target.closest && event.target.closest('.remove-link');
    if (!lien || !lien.closest('#CartForm')) return;
    if (!lien.closest('.sbc-panier-tiroir')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    const ligne = lien.closest('.cart-line');
    if (!ligne) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    ligne.dataset.lineQuantity = '0';
    ligne.classList.add('is-removing');
    annoncer('');
    schedule(ligne, 0);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    const cible = event.target;
    if (!cible || typeof cible.closest !== 'function') return;
    const input = cible.closest('input[name^="updates["]');
    if (!input || input.type === 'hidden') return;
    if (!input.closest('#CartForm')) return;
    if (!input.closest('.sbc-panier-tiroir')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    appliquerQuantite(input);
  }, true);

  /* Le moteur annonce un panier rendu : on réinstalle les commandes. */
  document.addEventListener('sbc:cart-updated', () => {
    enhanceSteppers();
    occupe(false);

    /* Focus rendu au champ de la ligne modifiée. Le rendu de section restitue
       déjà le focus par identifiant, mais cet identifiant dérive de la clé de
       ligne, qui change dès que la quantité change : la cible est donc
       retrouvée par la variante, stable. */
    if (!focusARendre) return;
    const ligne = document.querySelector(`.cart-line[data-line-variant="${focusARendre}"]`);
    const commande = focusCommande;
    const bouton = commande && ligne
      ? ligne.querySelector(`[data-sbc-qty="${commande}"]:not([disabled])`)
      : null;
    const champ = ligne && ligne.querySelector('input[name^="updates["]:not([type="hidden"])');
    /* Le bouton pressé d'abord ; le champ en repli quand la ligne a changé de
       forme (quantité 1, bouton retiré) ou quand la quantité a été saisie. */
    const cible = bouton || champ;
    focusARendre = null;
    focusCommande = null;
    if (cible && document.activeElement !== cible) {
      cible.focus({ preventScroll: true });
      if (cible === champ && typeof cible.select === 'function') cible.select();
    }
  });

  enhanceSteppers();
  /* Section neuve injectée (tiroir, ou rendu par le moteur) : les boutons de
     quantité se réinstallent sur le DOM neuf. */
  document.addEventListener('sbc:section-rendue', enhanceSteppers);

  /* ------------------------------------------------------------------
     Pied collant de `/cart` sous 750 px — lot 132.

     Même règle que la barre d'achat de la fiche produit : le rappel ne
     paraît QUE lorsque le bouton réel du résumé est hors écran. Le
     `rootMargin` négatif retire du champ d'observation la hauteur du pied
     lui-même : sans lui, le bouton réel serait déclaré « visible » alors
     qu'il est encore caché derrière la barre, et les deux se superposeraient
     le temps d'un défilement.

     La barre n'est cherchée que sous `#MainContent` : le tiroir a son propre
     pied fixe et ne doit jamais en recevoir un second. `hidden` est l'état
     du HTML (sans JavaScript, rien n'apparaît) ; `is-visible` est l'état
     piloté au défilement, le seul qui s'anime.
     ------------------------------------------------------------------ */
  const seuilPiedCollant = window.matchMedia('(max-width: 749px)');
  const HAUTEUR_PIED = 78;
  let observateurPied = null;

  const majPiedCollant = () => {
    if (observateurPied) { observateurPied.disconnect(); observateurPied = null; }
    const barre = document.querySelector('#MainContent [data-sbc-cart-sticky]');
    const effacer = () => {
      if (barre) { barre.classList.remove('is-visible'); barre.hidden = true; }
      document.body.classList.remove('sbc-cart-sticky-on');
    };
    if (!barre) { effacer(); return; }
    const reel = document.querySelector('#MainContent #CartForm .cart-checkout');
    if (!reel || !seuilPiedCollant.matches || typeof IntersectionObserver !== 'function') {
      effacer();
      return;
    }
    barre.hidden = false;
    observateurPied = new IntersectionObserver((entrees) => {
      const boutonReelVisible = entrees.some((e) => e.isIntersecting);
      barre.classList.toggle('is-visible', !boutonReelVisible);
      document.body.classList.toggle('sbc-cart-sticky-on', !boutonReelVisible);
    }, { threshold: 0, rootMargin: `0px 0px -${HAUTEUR_PIED}px 0px` });
    observateurPied.observe(reel);
  };

  majPiedCollant();
  document.addEventListener('sbc:section-rendue', majPiedCollant);
  document.addEventListener('sbc:cart-updated', majPiedCollant);
  if (typeof seuilPiedCollant.addEventListener === 'function') {
    seuilPiedCollant.addEventListener('change', majPiedCollant);
  }

  /* Le moteur a essuyé un refus de Shopify — stock insuffisant, quantité
     plafonnée. Le message vient de Shopify et s'affiche à la cliente, pas
     seulement dans la console. */
  document.addEventListener('sbc:panier-erreur', (event) => {
    const message = (event.detail && event.detail.message) || '';
    if (!message) return;
    annoncer(message);
    document.querySelectorAll('.cart-line.is-removing').forEach((l) => l.classList.remove('is-removing'));
    occupe(false);
  });
})();


/* SBC_CART_COMMERCE_V22 — résumé commercial et extras, consolidés. */
(() => {
  if (window.__sbcCartCommerceV3) return;
  window.__sbcCartCommerceV3 = true;

  const ROOT = window.Shopify?.routes?.root || '/';
  /* Lot 103 — ce module n'écrit plus le résumé.

     Il injectait un titre, une note de pression à l'achat (« Vos articles ne
     sont pas réservés… »), une ligne de livraison, un bloc de code promo, et
     il remplaçait le libellé du bouton de paiement. Tout cela est rendu par
     `sections/main-cart.liquid`, une seule fois, avant l'affichage. Il ne
     reste ici que le geste : envoyer un code à Shopify et en rendre compte. */

  let activeCode = '';
  let activeCodeConfirmed = false;
  let statusState = { text:'', kind:'' };
  let requeteEnVol = false;

  /* Lot 109 (A-30) — même convention que `snippets/sbc-prix.liquid` et
     `snippets/sbc-montant.liquid` (suffixe, virgule, espace normale) plutôt
     que `Intl.NumberFormat`, dont l'espace insécable (fine ou normale selon
     le moteur) ne correspond pas au rendu Liquid du reste du site. */

  /* Lot 104 — la lecture comme l'écriture passent par le moteur : c'est lui
     qui sérialise, et le panier qu'il rend est celui d'après réconciliation.
     Lire `/cart.js` en parallèle d'un cycle donnait un état intermédiaire. */
  const getCart = () => window.SbcPanier.lire();

  const normalizeCodeEntries = (cart) => {
    const raw = cart?.discount_codes ?? cart?.discountCodes ?? [];
    if (!Array.isArray(raw)) return [];
    return raw.map((entry) => {
      if (typeof entry === 'string') return { code:entry, applicable:true };
      return {code:String(entry?.code ?? entry?.title ?? '').trim(),applicable:entry?.applicable};
    }).filter((entry) => entry.code);
  };

  const findCodeState = (cart, code) => {
    const wanted = String(code || '').trim().toLowerCase();
    if (!wanted) return null;
    return normalizeCodeEntries(cart).find((entry) => entry.code.toLowerCase() === wanted) || null;
  };

  const collectDiscountTitles = (cart) => {
    const titles = [];
    const add = (value) => {
      const text = String(value || '').trim();
      if (!text || titles.some((item) => item.toLowerCase() === text.toLowerCase())) return;
      titles.push(text);
    };
    [cart?.cart_level_discount_applications,cart?.discount_applications,cart?.discountApplications].forEach((group) => {
      if (!Array.isArray(group)) return;
      group.forEach((item) => add(item?.title ?? item?.code));
    });
    if (Array.isArray(cart?.items)) {
      cart.items.forEach((item) => {
        if (Array.isArray(item?.discounts)) item.discounts.forEach((discount) => add(discount?.title ?? discount?.code));
        if (Array.isArray(item?.line_level_discount_allocations)) item.line_level_discount_allocations.forEach((allocation) => add(allocation?.discount_application?.title));
      });
    }
    return titles;
  };

  const codeAppearsInDiscounts = (cart, code) => {
    const wanted = String(code || '').trim().toLowerCase();
    if (!wanted) return false;
    return collectDiscountTitles(cart).some((title) => title.toLowerCase().includes(wanted));
  };

  const setStatus = (text = '', kind = '') => {
    statusState = { text, kind };
    document.querySelectorAll('.sbc-discount__status').forEach((status) => {
      status.textContent = text;
      status.classList.remove('is-error','is-success','is-neutral');
      if (kind) status.classList.add('is-' + kind);
    });
    document.querySelectorAll('.sbc-discount').forEach((block) => block.classList.toggle('is-error', kind === 'error'));
  };

  

  /* Lot 121 — Liquid rend DEUX formes du bloc, et lui seul décide laquelle :
     « appliqué » (une ligne « Code X · Retirer  −montant », sans champ de
     saisie) et « saisie » (le champ + « Valider »). Ce module ne fabrique
     plus la forme inverse : il n'a aucun montant à écrire — il n'en lit ni
     n'en calcule aucun — et il n'écrirait qu'un état transitoire, démenti au
     rendu suivant de la section. Il tolère donc l'absence du champ et se
     limite à ce qui lui appartient : l'état d'erreur et le message.

     Le seul rétablissement conservé est celui du bouton de la forme de
     saisie, que `setBusy` a mis à « ... » pendant l'aller-retour : quand
     Shopify refuse un code, rien n'est re-rendu et le bouton doit retrouver
     son libellé. */
  const syncDiscountControl = (summary) => {
    const block = summary?.querySelector('.sbc-discount');
    if (!block) return;
    const input = block.querySelector('.sbc-discount__input');
    const button = block.querySelector('.sbc-discount__button');
    const status = block.querySelector('.sbc-discount__status');
    const applique = Boolean((block.dataset.sbcCodeApplique || '').trim());
    block.classList.toggle('is-applied', applique);
    block.classList.toggle('is-error', statusState.kind === 'error');
    if (input && button && !applique) {
      input.readOnly = false;
      button.textContent = 'Valider';
      button.dataset.sbcDiscountAction = 'apply';
      button.setAttribute('aria-label', 'Valider le code de réduction');
    }
    if (!status) return;
    status.textContent = statusState.text;
    status.classList.remove('is-error','is-success','is-neutral');
    if (statusState.kind) status.classList.add('is-' + statusState.kind);
  };

  /* Le code promo réellement appliqué est écrit par Liquid sur le bloc
     (`data-sbc-code-applique`) : après chaque rendu de la section, c'est lui
     qui fait foi, pas une variable de ce module. Le garde `requeteEnVol`
     empêche seulement d'adopter un état périmé pendant l'aller-retour avec
     Shopify. Aucun montant n'est lu ni écrit ici. */
  const rafraichirResumes = () => {
    document.querySelectorAll('.cart-summary').forEach((summary) => {
      const block = summary.querySelector('.sbc-discount');
      if (block && !requeteEnVol) {
        const rendu = (block.dataset.sbcCodeApplique || '').trim();
        if (rendu !== activeCode) {
          activeCode = rendu;
          activeCodeConfirmed = Boolean(rendu);
        }
      }
      syncDiscountControl(summary);
    });
  };

  const setBusy = (block, busy) => {
    requeteEnVol = Boolean(busy);
    const button = block?.querySelector('.sbc-discount__button');
    const input = block?.querySelector('.sbc-discount__input');
    if (button) button.disabled = busy;
    if (input) input.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy && button) button.textContent = '...';
  };

  /* Le moteur renvoie le panier APRÈS réconciliation : c'est cet état-là qu'il
     faut lire pour dire si le code a produit un effet, puisqu'une remise
     automatique de la boutique change les totaux du même cycle. */
  const updateDiscount = (discountValue) => window.SbcPanier.mettreAJour({ discount: discountValue });

  const applyDiscount = async (block) => {
    const input = block?.querySelector('.sbc-discount__input');
    const code = input?.value.trim();
    if (!input || !code) {
      setStatus('Entrez un code de réduction avant de le valider.', 'error');
      input?.focus();
      return;
    }
    setBusy(block, true);
    setStatus('', '');
    try {
      const before = await getCart();
      const after = await updateDiscount(code);
      const explicitState = findCodeState(after, code);
      const economicsChanged = Number(after.total_discount || 0) > Number(before.total_discount || 0) || Number(after.total_price || 0) < Number(before.total_price || 0);
      const titleMatch = codeAppearsInDiscounts(after, code);
      if (explicitState?.applicable === false) {
        activeCode = '';
        activeCodeConfirmed = false;
        /* Mesuré : dès qu'une remise s'applique déjà au panier, Shopify refuse
           le code. Depuis D19 (12 septembre 2026) le thème n'offre plus aucun
           cadeau automatique — ni carnet, ni vidéo : la seule cause restante
           est une remise automatique de la boutique déjà déduite du panier.
           Le message dit cette cause plutôt que de laisser la cliente croire à
           un code invalide. Aucune règle de cumul n'est modifiée ici. */
        const remiseDejaAppliquee = Number(after.total_discount || 0) > 0;
        setStatus(remiseDejaAppliquee
          ? 'Ce code ne se cumule pas avec la remise déjà appliquée à votre panier : une seule remise s’applique par commande.'
          : 'Ce code n’est pas applicable à ce panier.', 'error');
        return;
      }
      activeCode = code;
      activeCodeConfirmed = explicitState?.applicable === true || titleMatch || economicsChanged;
      if (activeCodeConfirmed) {
        /* Lot 121 — le message ne chiffre plus rien. Il annonçait
           « Réductions totales : X » avec `cart.total_discount`, c'est-à-dire
           le code ET les cadeaux : au banc, 25,04 € pour un code qui valait
           5,05 €. Le montant réel du code est désormais rendu par Liquid,
           juste au-dessus, sur sa propre ligne — ce module n'a plus aucun
           montant à lire ni à écrire. */
        setStatus('Code appliqué à votre panier.', 'success');
      } else {
        setStatus('Code enregistré. S’il concerne la livraison, son effet apparaîtra au paiement.', 'neutral');
      }
    } catch (error) {
      console.error('[Scoop] Code promo panier:', error);
      activeCode = '';
      activeCodeConfirmed = false;
      setStatus('Impossible d’appliquer ce code pour le moment.', 'error');
    } finally {
      setBusy(block, false);
      document.querySelectorAll('.cart-summary').forEach(syncDiscountControl);
    }
  };

  const removeDiscount = async (block) => {
    if (!activeCode) return;
    const removedCode = activeCode;
    setBusy(block, true);
    setStatus('', '');
    try {
      const cart = await updateDiscount('');
      const stillApplied = findCodeState(cart, removedCode);
      if (stillApplied?.applicable === true) throw new Error('Le code est toujours appliqué');
      activeCode = '';
      activeCodeConfirmed = false;
      setStatus(Number(cart.total_discount || 0) > 0 ? 'Code retiré. Les remises automatiques éventuelles restent appliquées.' : 'Code de réduction retiré.', 'neutral');
    } catch (error) {
      console.error('[Scoop] Retrait code promo:', error);
      setStatus('Impossible de retirer ce code pour le moment.', 'error');
    } finally {
      setBusy(block, false);
      document.querySelectorAll('.cart-summary').forEach(syncDiscountControl);
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.sbc-discount__button');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const block = button.closest('.sbc-discount');
    if (button.dataset.sbcDiscountAction === 'remove') removeDiscount(block);
    else applyDiscount(block);
  });

  document.addEventListener('keydown', (event) => {
    const input = event.target.closest('.sbc-discount__input');
    if (!input || event.key !== 'Enter' || input.readOnly) return;
    event.preventDefault();
    applyDiscount(input.closest('.sbc-discount'));
  });

  rafraichirResumes();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rafraichirResumes, { once:true });
  }

  /* V15 — le moteur de cadeaux (`assets/sbc-cadeaux.js`) est le seul à rendre
     la section panier, et il annonce chaque rendu. On s'y raccroche plutôt que
     d'observer le DOM : l'ancien observateur était posé sur la section trouvée
     au chargement, il ne se posait donc jamais quand la page démarrait avec un
     panier vide, et le résumé restait sans détail de réduction après un ajout. */
  document.addEventListener('sbc:cart-updated', rafraichirResumes);
  /* Une section injectée dans le tiroir ou rendue par le moteur : le détail
     des réductions se relit sur le DOM neuf. Les steppers, eux, sont
     réinstallés par la première partie de ce fichier, qui écoute le même
     événement. */
  document.addEventListener('sbc:section-rendue', rafraichirResumes);
})();
